import { translationKey } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import SingleLineText from './ui/SingleLineText';
import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MedicationIcon from '@mui/icons-material/Medication';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import useKeyboard from '../hooks/useKeyboard';
import { colors } from '../design/tokens';
import useAppLanguage from '../i18n';

const navItems = [
    { label: translationKey('홈'), icon: HomeIcon, path: '/' },
    { label: translationKey('약 목록'), icon: MedicationIcon, path: '/medications' },
    { label: translationKey('캘린더'), icon: CalendarMonthIcon, path: '/calendar' },
    { label: translationKey('설정'), icon: SettingsIcon, path: '/settings' },
];

export default function BottomNav() {
    const { language, t } = useAppLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const settingsTapRef = useRef({ count: 0, timer: null });
    const { keyboardOpen } = useKeyboard();

    if (location.pathname.includes('/new') || location.pathname.includes('/edit') || location.pathname.includes('/onboarding') || location.pathname === '/chat') {
        return null;
    }

    if (keyboardOpen) return null;

    const currentIndex = navItems.findIndex((item) =>
        item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
    );

    return (
        <Box sx={{
            position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: 480, zIndex: 1000,
            bgcolor: colors.surface, borderTop: `1px solid ${colors.borderNeutral}`,
            display: 'flex', justifyContent: 'space-around', alignItems: 'stretch',
            // 시스템 글자/디스플레이 크기에 따라 늘어날 수 있도록 minHeight 사용.
            // 시스템 nav bar 인셋은 SafeAreaView/AdContainer 가 처리하므로 여기선 시각 여백만.
            minHeight: 56,
            pb: '4px',
        }}>
            {navItems.map((item, idx) => {
                const Icon = item.icon;
                const selected = idx === currentIndex;
                return (
                    <Box
                        key={item.path}
                        component="button"
                        type="button"
                        onClick={() => {
                            if (item.path === '/') {
                                window.dispatchEvent(new CustomEvent('pill-reminder-home-tab'));
                            }
                            if (item.path === '/settings') {
                                const ref = settingsTapRef.current;
                                ref.count++;
                                clearTimeout(ref.timer);
                                if (ref.count >= 5) {
                                    ref.count = 0;
                                    navigate('/admin');
                                    return;
                                }
                                ref.timer = setTimeout(() => { ref.count = 0; }, 2000);
                            }
                            navigate(item.path);
                        }}
                        sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', flex: 1, minWidth: 0, py: 0.6, px: 0.5,
                            border: 0, bgcolor: 'transparent', font: 'inherit',
                            appearance: 'none', WebkitAppearance: 'none',
                            WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                            transition: 'all 0.15s ease',
                            '&:active': { transform: 'scale(0.92)' },
                        }}
                    >
                        <Icon sx={{
                            fontSize: 24,
                            color: selected ? colors.brandText : '#888',
                            transition: 'color 0.15s ease',
                            flexShrink: 0,
                        }} />
                        <SingleLineText
                            minFontSize={language === 'ko' ? 12 : 10}
                            sx={{
                                fontSize: '0.78rem', mt: 0.2,
                                fontWeight: selected ? 700 : 400,
                                color: selected ? colors.brandText : '#888',
                                width: '100%', textAlign: 'center',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                        >
                            {language === 'en' && item.path === '/medications' ? t('Meds') : t(item.label)}
                        </SingleLineText>
                    </Box>
                );
            })}
        </Box>
    );
}
